import { Card, Col, Flex, Row, Space, Typography } from "antd";
import { Col16, Col24, Col8, } from "src/components/Columns";
import { StatsCard } from "../StatsCard";
import { PageCard } from "src/common/styles/styles";
import { DashboardData } from "src/common/models/report";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/state/reducer";
import { serviceLoading } from "src/common/apiLoader/state/selection";
import { GET_DASHBOARD_DATA_API } from "src/features/Report/state/actionTypes";
import { getDashboardDataApi } from "src/features/Report/state/action";
import { useEffect, useState } from "react";
import { SystemConstants } from "src/common/constants";
import { formatToCurrency } from "src/common/util";
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { getServiceContractsApi } from "src/features/ServiceContract/state/action";
import { getServiceCategoriesApi } from "src/features/Service/state/action";
import { ServiceContract } from "src/common/models/contract";
import { ServiceCategory } from "src/common/models/serviceCategory";
import { SystemDescriptions } from "src/common/descriptions";
import { Urls } from "src/common/urls";
import { useNavigate } from "react-router";
import { RangePickerComponent, RangePickerDates } from "src/components/SearchableTable/RangePickerComponent";
import dayjs from 'dayjs';
import { FlexDiv } from "src/common/styles/div";
import { FuturePayments } from "./FuturePayments";

ChartJS.register(ArcElement, Tooltip, Legend);

interface ReduxProps {
    dashboardData?: DashboardData
    isGettingDashboardData: boolean
    serviceContracts?: ServiceContract[]
    serviceCategories?: ServiceCategory[]
}

interface CardelementProps {
    title: string
    value: string
    secondaryDescription: string
    difference: number
    asCurrency?: boolean
    text: string
    hideDiff?: boolean
    onActionClick?: VoidFunction
}

interface SummaryData {
    field: string
    value: number
}

export const Statistics = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [pieChartData, setPieChartData] = useState<SummaryData[]>([])
    const [dateRanges, setDateRanges] = useState<RangePickerDates>({
        startDate: dayjs().startOf('month'),
        endDate: dayjs(),
    })

    const { Text } = Typography;

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        dashboardData: state.report.dashboardData,
        isGettingDashboardData: serviceLoading(state, [GET_DASHBOARD_DATA_API]),
        serviceCategories: state.service.serviceCategories,
        serviceContracts: state.serviceContract.serviceContracts,
    }))

    const descriptions = SystemDescriptions.COMPONENTS.DASHBOARD

    useEffect(() => {
        dispatch(getServiceContractsApi({
            isActive: true,
        }))

        dispatch(getServiceCategoriesApi())
    }, [])

    useEffect(() => {
        if (dateRanges) {
            dispatch(getDashboardDataApi(
                dateRanges.startDate.format(SystemConstants.DATETIME_QUERY(true)),
                dateRanges.endDate.format(SystemConstants.DATETIME_QUERY(false)),
            ))
        }
    }, [dateRanges])


    useEffect(() => {
        if (reduxProps.serviceCategories && reduxProps.serviceContracts) {
            const summaryData = {}

            reduxProps.serviceContracts.forEach(serviceContract => {
                const categoriesInContract: string[] = serviceContract.categoriesInDetailIds.split(",")
                categoriesInContract.forEach(categoryId => {
                    const foundService = reduxProps?.serviceCategories?.find(candidate => candidate.id === Number(categoryId))

                    if (foundService) {
                        if (summaryData[foundService.name!]) {
                            summaryData[foundService.name!]["qty"] += 1
                            summaryData[foundService.name!]["amount"] += serviceContract.total
                        } else {
                            summaryData[foundService.name!] = {
                                qty: 1,
                                amount: serviceContract.total,
                            }
                        }
                    }
                })
            })

            const newData: SummaryData[] = []
            Object.keys(summaryData).forEach(key => {
                newData.push({
                    field: `${key}-${summaryData[key]["qty"]} cts`,
                    value: summaryData[key]["amount"],
                })
            })

            setPieChartData(newData)
        }
    }, [reduxProps.serviceCategories, reduxProps.serviceContracts])

    const renderCardElement = (props: CardelementProps) => {
        return <StatsCard
            title={props.title}
            diff={Number(props.difference.toFixed(2))}
            value={props.value}
            secondaryValueData={props.secondaryDescription}
            style={{ height: '100%' }}
            asCurrency={props.asCurrency}
            text={props.text}
            hideDiff={props.hideDiff}
            onActionClick={props.onActionClick}
        />
    }

    const redirectToPage = (type: string) => {
        const dictionaryOfUrls = {
            ACCOUNT_RECEIVABLES: Urls.FRONTEND.SERVICE_CONTRACT_DEBIT.SERVICE_CONTRAC_DEBITS,
            ACCOUNT_PAYABLES: Urls.FRONTEND.PAYABLE_CONTRACT_DEBIT.PAYABLE_CONTRACT_DEBITS,
            SERVICE_CONTRACTS: Urls.FRONTEND.SERVICE_CONTRACT.SERVICE_CONTRACTS,
            PAYABLE_CONTRACTS: Urls.FRONTEND.ACCOUNT_PAYABLE.PAYABLE_CONTRACTS,
            EXPENDITURE_RECORDS: Urls.FRONTEND.EXPENDITURE_RECORD.RECORDS,
            CASHBOX_STMT: Urls.FRONTEND.CASHBOX_STATEMENT.RECORDS,
        }

        if (dictionaryOfUrls[type]) {
            navigate(dictionaryOfUrls[type])
        }
    }

    const renderStatistics = () => (
        <Row gutter={[12, 8]} >
            <Col8>
                {renderCardElement(
                    {
                        title: descriptions.STATISTICS.ACCOUNT_RECEIVABLES_TOTAL.TITLE,
                        value: formatToCurrency(reduxProps.dashboardData?.statistics?.accountReceivables?.amount.toString() || '0'),
                        secondaryDescription: ``,
                        difference: 0,
                        asCurrency: true,
                        text: descriptions.STATISTICS.ACCOUNT_RECEIVABLES_TOTAL.TEXT,
                        onActionClick: () => redirectToPage("ACCOUNT_RECEIVABLES"),
                        hideDiff: true,
                    }
                )}
            </Col8>
            <Col8>
                {renderCardElement(
                    {
                        title: descriptions.STATISTICS.ACCOUNT_PAYABLES_TOTAL.TITLE,
                        value: formatToCurrency(reduxProps.dashboardData?.statistics?.accountPayables?.amount.toString() || '0'),
                        secondaryDescription: ``,
                        difference: 0,
                        asCurrency: true,
                        text: descriptions.STATISTICS.ACCOUNT_PAYABLES_TOTAL.TEXT,
                        onActionClick: () => redirectToPage("ACCOUNT_PAYABLES"),
                        hideDiff: true,
                    }
                )}
            </Col8>
            <Col8>
                {renderCardElement(
                    {
                        title: descriptions.STATISTICS.ACCOUNT_RECEIVABLES.TITLE,
                        value: formatToCurrency(reduxProps.dashboardData?.statistics?.accountReceivables?.range.toString() || '0'),
                        secondaryDescription: `${formatToCurrency(reduxProps.dashboardData?.statistics?.accountReceivables?.amountPayed.toString() || '0')} ${descriptions.STATISTICS.ACCOUNT_RECEIVABLES.SECONDARY}`,
                        difference: reduxProps.dashboardData?.statistics?.accountReceivables?.amountPayed ? (reduxProps.dashboardData?.statistics?.accountReceivables?.amountPayed / reduxProps.dashboardData?.statistics?.accountReceivables?.amount) * 100 : 0,
                        asCurrency: true,
                        text: descriptions.STATISTICS.ACCOUNT_RECEIVABLES.TEXT,
                        onActionClick: () => redirectToPage("ACCOUNT_RECEIVABLES"),
                    }
                )}
            </Col8>
            <Col8>
                {renderCardElement(
                    {
                        title: descriptions.STATISTICS.ACCOUNT_PAYABLES.TITLE,
                        value: formatToCurrency(reduxProps.dashboardData?.statistics?.accountPayables?.range.toString() || '0'),
                        secondaryDescription: `${formatToCurrency(reduxProps.dashboardData?.statistics?.accountPayables?.amountPayed.toString() || '0')} ${descriptions.STATISTICS.ACCOUNT_PAYABLES.SECONDARY}`,
                        difference: reduxProps.dashboardData?.statistics?.accountPayables?.amountPayed ? (reduxProps.dashboardData?.statistics?.accountPayables?.amountPayed / reduxProps.dashboardData?.statistics?.accountPayables?.amount) * 100 : 0,
                        asCurrency: true,
                        text: descriptions.STATISTICS.ACCOUNT_PAYABLES.TEXT,
                        onActionClick: () => redirectToPage("ACCOUNT_PAYABLES"),
                    }
                )}
            </Col8>
            <Col8>
                {renderCardElement(
                    {
                        title: descriptions.STATISTICS.EXPENDITURE_RECORDS.TITLE,
                        value: formatToCurrency(reduxProps.dashboardData?.statistics?.finance?.expenditureRecordsTotal.toString() || '0'),
                        secondaryDescription: ``,
                        difference: 0,
                        hideDiff: true,
                        asCurrency: true,
                        text: descriptions.STATISTICS.EXPENDITURE_RECORDS.TEXT,
                        onActionClick: () => redirectToPage("EXPENDITURE_RECORDS"),
                    }
                )}
            </Col8>
            <Col8>
                {renderCardElement(
                    {
                        title: descriptions.STATISTICS.SERVICE_CONTRACTS.TITLE,
                        value: formatToCurrency(reduxProps.dashboardData?.statistics?.customerContracts?.amount.toString() || '0'),
                        secondaryDescription: `${reduxProps.dashboardData?.statistics?.customerContracts?.numberOfContracts} ${descriptions.STATISTICS.SERVICE_CONTRACTS.SECONDARY}`,
                        difference: 0,
                        hideDiff: true,
                        asCurrency: true,
                        text: descriptions.STATISTICS.SERVICE_CONTRACTS.TEXT,
                        onActionClick: () => redirectToPage("SERVICE_CONTRACTS"),
                    }
                )}
            </Col8>
            <Col8>
                {renderCardElement(
                    {
                        title: descriptions.STATISTICS.PAYABLE_CONTRACTS.TITLE,
                        value: formatToCurrency(reduxProps.dashboardData?.statistics?.payableContracts?.amount.toString() || '0'),
                        secondaryDescription: `${reduxProps.dashboardData?.statistics?.payableContracts?.numberOfContracts} ${descriptions.STATISTICS.PAYABLE_CONTRACTS.SECONDARY}`,
                        difference: 0,
                        hideDiff: true,
                        asCurrency: true,
                        text: descriptions.STATISTICS.PAYABLE_CONTRACTS.TEXT,
                        onActionClick: () => redirectToPage("PAYABLE_CONTRACTS"),
                    }
                )}
            </Col8>
            <Col8>
                {renderCardElement(
                    {
                        title: descriptions.STATISTICS.CASHBOX_BALANCE.TITLE,
                        value: formatToCurrency(reduxProps.dashboardData?.statistics?.finance?.cashboxBalance.toString() || '0'),
                        secondaryDescription: ``,
                        difference: 0,
                        hideDiff: true,
                        asCurrency: true,
                        text: descriptions.STATISTICS.CASHBOX_BALANCE.TEXT,
                        onActionClick: () => redirectToPage("CASHBOX_STMT"),
                    }
                )}
            </Col8>
        </Row>
    )

    const renderPieChart = () => {

        const data = {
            labels: pieChartData.map(data => data.field),
            datasets: [
                {
                    label: descriptions.SERVICE_DISTRIBUTION.DETAIL_LABEL,
                    data: pieChartData.map(data => data.value),
                    backgroundColor: [
                        'rgba(0, 55, 109, 0.2)',
                        'rgba(255, 0, 48, 0.2)',
                        'rgba(255, 159, 64, 0.2)',
                        'rgba(80, 80, 80, 0.2)',
                        'rgba(75, 192, 192, 0.2)',
                        'rgba(153, 102, 255, 0.2)',
                    ],
                    borderColor: [
                        'rgba(0, 55, 109, 1)',
                        'rgba(255, 0, 48, 1)',
                        'rgba(255, 159, 64, 1)',
                        'rgba(80, 80, 80, 1)',
                        'rgba(75, 192, 192, 1)',
                        'rgba(153, 102, 255, 1)',
                    ],
                    borderWidth: 1,
                },
            ],
        };

        return <PageCard
            title={descriptions.SERVICE_DISTRIBUTION.TITLE}
            size="small"
        >
            <div style={{ width: "100%", height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Doughnut data={data} />
            </div>
        </PageCard>
    }

    const renderNextPayments = () => {
        return <FuturePayments
            title={descriptions.FUTURE_PAYABLES.TITLE}
            data={reduxProps.dashboardData?.nextPayments || []}
        />
    }

    const renderBalance = () => {

        const incomes: number = reduxProps.dashboardData?.statistics?.accountReceivables?.amountPayed || 0
        const outcomes: number = (reduxProps.dashboardData?.statistics?.accountPayables?.amountPayed || 0)
            +
            (reduxProps.dashboardData?.statistics?.finance?.expenditureRecordsTotal || 0)

        const balance: number = incomes - outcomes

        return <Card
            title={descriptions.GENERAL_BALANCE.TITLE}
            size="small"
        >
            <Flex vertical gap="middle" justify="center">
                <Typography.Title
                    style={{
                        margin: 0,
                        color: balance >= 0 ? "#389e0d" : "#820014",
                    }}
                >
                    {formatToCurrency(balance.toString(), "Q")}
                </Typography.Title>
                <Row>
                    <Col sm={24} lg={8}>
                        <Space direction="vertical">
                            <Text>{descriptions.GENERAL_BALANCE.INCOMES}</Text>
                            <Text type="secondary">{descriptions.GENERAL_BALANCE.ACCOUNTS_RECEIVED}</Text>
                        </Space>
                    </Col>
                    <Col sm={24} lg={8}>
                        <Space direction="vertical">
                            <Text className="text-end" strong>
                                {
                                    formatToCurrency(incomes.toString(), "Q")
                                }
                            </Text>
                            <Text type="secondary">{
                                formatToCurrency(reduxProps.dashboardData?.statistics?.accountReceivables?.amountPayed.toString() || '0', "Q")
                            }</Text>
                        </Space>
                    </Col>
                </Row>
                <Row>
                    <Col sm={24} lg={8}>
                        <Space direction="vertical">
                            <Text>{descriptions.GENERAL_BALANCE.OUTCOMES}</Text>
                            <Text type="secondary">{descriptions.GENERAL_BALANCE.ACCOUNTS_PAYED}</Text>
                            <Text type="secondary">{descriptions.GENERAL_BALANCE.EXPENDITURES}</Text>
                        </Space>
                    </Col>
                    <Col sm={24} lg={8}>
                        <Space direction="vertical">
                            <Text className="text-end" strong>
                                {
                                    formatToCurrency(outcomes.toString())
                                }
                            </Text>
                            <Text type="secondary">{
                                formatToCurrency(reduxProps.dashboardData?.statistics?.accountPayables?.amountPayed.toString() || '0', "Q")
                            }</Text>
                            <Text type="secondary">{
                                formatToCurrency(reduxProps.dashboardData?.statistics?.finance?.expenditureRecordsTotal.toString() || '0', "Q")
                            }</Text>
                        </Space>
                    </Col>
                </Row>
            </Flex>
        </Card>
    }

    const handleRangePickerChange = (e) => {
        setDateRanges({
            startDate: e.startDate,
            endDate: e.endDate,
        })
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12, }}>
            <Row gutter={[12, 8]}>
                <Col16>
                    <FlexDiv style={{ height: '100%', marginTop: 8 }}>
                        Datos para fecha {dateRanges.startDate.format("DD-MM-YYYY")} al {dateRanges.endDate.format("DD-MM-YYYY")}
                    </FlexDiv>
                </Col16>
                <Col8>
                    <RangePickerComponent
                        rangePickerDefaultValues={[
                            dateRanges.startDate,
                            dateRanges.endDate,
                        ]}
                        rangePickerOnChange={handleRangePickerChange}
                    />
                </Col8>
                <Col24>
                    {renderStatistics()}
                </Col24>
            </Row>
            <Row gutter={[12, 8]}>
                <Col8>
                    {renderPieChart()}
                </Col8>
                <Col16>
                    {renderBalance()}
                </Col16>
                <Col24>
                    {renderNextPayments()}
                </Col24>
            </Row>
        </div>
    )
}
